import React, { useEffect, useState } from 'react';
import { App, Alert, Button, Card, Collapse, Input, Result, Skeleton, Space, Typography } from 'antd';
import { QRCodeCanvas } from 'qrcode.react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useIsModuleEnabled } from '../../hooks/useIsModuleEnabled';
import { useResponsive } from '../../hooks/useResponsive';
import { MODULE_KEYS } from '../../constants/moduleKeys';
import { PLANTILLAS, resolveColores } from '../../constants/menuDigitalPlantillas';
import {
  menuDigitalApi,
  useGetMenuDigitalConfigQuery,
  useUpdateMenuSlugMutation,
  useGetMenuEditorQuery,
} from '../../services/menuDigitalApi';
import { useUpdateSettingMutation } from '../../services/settingsApi';
import MenuEditorCanvas from './MenuEditorCanvas';
import type { MenuPlantilla } from '../../types/menuDigital';

const { Text } = Typography;

// Pestaña "Menú Digital" dentro de Gestión del Menú. Vive aquí y no en
// Configuración porque el menú digital ES el menú: el editor necesita los
// productos a la mano, y esto se toca cada que cambia la carta, no una sola
// vez al dar de alta el negocio.

const PLANTILLA_KEY = 'MenuDigitalPlantilla';
const COLORES_KEY = 'MenuDigitalColores';
const LEMA_KEY = 'MenuDigitalLema';

// Espejo del regex del backend (MenuDigitalService.SlugRegex): 3-60, sin
// guion al inicio/fin. Si divergen, el usuario ve el error genérico del 400.
const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,58}[a-z0-9]$/;

const PaletaDots: React.FC<{ colores: string[]; size?: number }> = ({ colores, size = 14 }) => (
  <span style={{ display: 'inline-flex', gap: 3 }}>
    {colores.map((color, i) => (
      <span
        key={i}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: color,
          border: '1px solid var(--border-color, var(--bg-tertiary))',
          display: 'inline-block',
        }}
      />
    ))}
  </span>
);

const QrBlock: React.FC<{ titulo: string; url: string; archivo: string }> = ({ titulo, url, archivo }) => {
  const qrId = `qr-${archivo}`;

  const descargar = () => {
    const canvas = document.getElementById(qrId) as HTMLCanvasElement | null;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${archivo}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <Card size="small" style={{ textAlign: 'center' }}>
      <Text strong>{titulo}</Text>
      <div style={{ margin: '12px 0', display: 'inline-block', lineHeight: 0, borderRadius: 8, overflow: 'hidden' }}>
        {/* El canvas pinta su propio fondo blanco (default de la librería) con
            zona de silencio — el QR necesita fondo claro para escanearse bien
            incluso con la app en tema oscuro. */}
        <QRCodeCanvas id={qrId} value={url} size={180} marginSize={3} />
      </div>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-all' }}>{url}</Text>
        <Space wrap size={4}>
          <Button size="small" onClick={descargar}>QR en PNG</Button>
          <Button size="small" type="link" href={url} target="_blank">
            Abrir
          </Button>
          {/* Abre la página real con ?imprimir=1, que dispara el diálogo de
              impresión sola. Se imprime la página, no una copia, para que el
              PDF salga con la plantilla y los colores elegidos. */}
          <Button size="small" type="link" href={`${url}?imprimir=1`} target="_blank">
            PDF
          </Button>
        </Space>
      </Space>
    </Card>
  );
};

const MenuDigitalTab: React.FC = () => {
  const { message } = App.useApp();
  const { isMobile } = useResponsive();
  const dispatch = useAppDispatch();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const restaurantId = currentRestaurant?.id ?? '';

  const { enabled: moduleEnabled, isLoading: moduleLoading } = useIsModuleEnabled(MODULE_KEYS.MenuDigital);
  const { data: config, isLoading: configLoading } = useGetMenuDigitalConfigQuery(restaurantId, {
    skip: !restaurantId || !moduleEnabled,
  });
  const { data: editorMenu, isLoading: editorLoading } = useGetMenuEditorQuery(restaurantId, {
    skip: !restaurantId || !moduleEnabled,
  });
  const [updateSlug, { isLoading: savingSlug }] = useUpdateMenuSlugMutation();
  const [updateSetting] = useUpdateSettingMutation();

  const [slugDraft, setSlugDraft] = useState('');
  const [lemaDraft, setLemaDraft] = useState('');

  // Sincronizar contra el VALOR del servidor, no contra el objeto config:
  // guardar plantilla, paleta o lema invalida AppSettings y refetchea, y
  // depender de `config` borraría lo que el usuario esté escribiendo.
  useEffect(() => {
    setSlugDraft(config?.slug ?? '');
  }, [config?.slug]);

  useEffect(() => {
    setLemaDraft(config?.lema ?? '');
  }, [config?.lema]);

  if (moduleLoading || (moduleEnabled && configLoading)) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  if (!moduleEnabled) {
    return (
      <Result
        status="403"
        title="Menú Digital no habilitado"
        subTitle="Este módulo es parte del plan Profesional. Contacta a soporte si quieres habilitarlo."
      />
    );
  }

  const plantilla: MenuPlantilla = config?.plantilla ?? 'carta';
  const coloresActuales = resolveColores(plantilla, config?.colores ?? []);
  const origin = window.location.origin;
  const urlCarta = config?.slug ? `${origin}/menu/${config.slug}` : null;

  const guardarSlug = async () => {
    const normalized = slugDraft.trim().toLowerCase();
    if (!SLUG_REGEX.test(normalized)) {
      message.error('La dirección solo puede llevar minúsculas, números y guiones (3 a 60 caracteres).');
      return;
    }
    try {
      await updateSlug({ restaurantId, slug: normalized }).unwrap();
      message.success('Dirección guardada. El QR ya apunta a tu menú.');
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 409) {
        message.error('Esa dirección ya la usa otro restaurante. Elige otra.');
      } else if (status === 400) {
        message.error('La dirección solo puede llevar minúsculas, números y guiones (3 a 60 caracteres).');
      } else {
        message.error('No se pudo guardar la dirección.');
      }
    }
  };

  // La plantilla, la paleta y el lema son settings genéricas, que solo
  // invalidan AppSettings — pero el lienzo se alimenta de getMenuEditor, con su
  // propia etiqueta. Sin este empujón el editor seguía pintando los colores
  // viejos, justo debajo del texto que promete que el cambio se ve al instante.
  const refrescarLienzo = () => {
    dispatch(menuDigitalApi.util.invalidateTags(['MenuEditor']));
  };

  const guardarSetting = async (key: string, value: string, ok: string) => {
    try {
      await updateSetting({ key, value }).unwrap();
      refrescarLienzo();
      message.success(ok);
    } catch {
      message.error('No se pudo guardar el cambio.');
    }
  };

  const elegirPlantilla = async (nueva: MenuPlantilla) => {
    if (nueva === plantilla) return;
    // Cambiar de plantilla resetea la paleta a la default: los 4 slots
    // significan cosas distintas en cada plantilla. La paleta va PRIMERO y con
    // await — si el reset falla, no cambiamos de plantilla y no queda una
    // paleta vieja mapeada a slots ajenos.
    try {
      await updateSetting({ key: COLORES_KEY, value: '[]' }).unwrap();
      await updateSetting({ key: PLANTILLA_KEY, value: nueva }).unwrap();
      refrescarLienzo();
      message.success(`Plantilla ${PLANTILLAS[nueva].nombre} elegida.`);
    } catch {
      message.error('No se pudo cambiar la plantilla. Intenta de nuevo.');
    }
  };

  const direccion = (
    <>
      <Space.Compact style={{ width: '100%', maxWidth: 560 }}>
        <Input
          addonBefore={isMobile ? '/menu/' : `${origin}/menu/`}
          value={slugDraft}
          onChange={(e) => setSlugDraft(e.target.value)}
          placeholder="mi-restaurante"
          maxLength={60}
        />
        <Button type="primary" loading={savingSlug} onClick={guardarSlug}>
          Guardar
        </Button>
      </Space.Compact>
      <div style={{ marginTop: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Minúsculas, números y guiones. Esta dirección va impresa en tus QRs: cambiarla invalida
          los QRs que ya imprimiste.
        </Text>
      </div>
    </>
  );

  const apariencia = (
    <>
      <Space wrap size={12}>
        {(Object.keys(PLANTILLAS) as MenuPlantilla[]).map((key) => {
          const info = PLANTILLAS[key];
          const activa = key === plantilla;
          return (
            <Card
              key={key}
              size="small"
              hoverable
              onClick={() => elegirPlantilla(key)}
              style={{
                width: isMobile ? '100%' : 260,
                borderColor: activa ? 'var(--color-primary)' : undefined,
                boxShadow: activa ? '0 0 0 1px var(--color-primary)' : undefined,
              }}
            >
              <Space direction="vertical" size={6}>
                <Text strong>{info.nombre}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>{info.descripcion}</Text>
                <PaletaDots colores={info.paletas[0].colores} />
              </Space>
            </Card>
          );
        })}
      </Space>

      <div style={{ marginTop: 20 }}>
        <Text strong>Paleta de colores</Text>
        <div style={{ marginTop: 8 }}>
          <Space wrap size={8}>
            {PLANTILLAS[plantilla].paletas.map((paleta) => {
              const activa = paleta.colores.every((c, i) => c === coloresActuales[i]);
              return (
                <Button
                  key={paleta.nombre}
                  onClick={() =>
                    guardarSetting(
                      COLORES_KEY,
                      JSON.stringify(paleta.colores),
                      `Paleta ${paleta.nombre} elegida.`
                    )
                  }
                  style={{
                    height: 'auto',
                    padding: '6px 12px',
                    borderColor: activa ? 'var(--color-primary)' : undefined,
                    boxShadow: activa ? '0 0 0 1px var(--color-primary)' : undefined,
                  }}
                >
                  <Space size={8}>
                    <PaletaDots colores={paleta.colores} />
                    <span>{paleta.nombre}</span>
                  </Space>
                </Button>
              );
            })}
          </Space>
        </div>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          Los colores se eligen por paleta completa para que tu menú siempre se vea bien. Los cambios
          se ven abajo al instante.
        </Text>
      </div>

      <div style={{ marginTop: 20 }}>
        <Text strong>Lema</Text>
        <Space.Compact style={{ width: '100%', maxWidth: 560, marginTop: 8 }}>
          <Input
            value={lemaDraft}
            onChange={(e) => setLemaDraft(e.target.value)}
            placeholder="cocina de casa desde 2012"
            maxLength={120}
          />
          <Button onClick={() => guardarSetting(LEMA_KEY, lemaDraft.trim(), 'Lema guardado.')}>
            Guardar
          </Button>
        </Space.Compact>
      </div>
    </>
  );

  const codigos = urlCarta ? (
    <>
      <Space wrap size={16} align="start">
        <QrBlock titulo="Carta completa" url={urlCarta} archivo="qr-menu" />
        <QrBlock titulo="Solo el menú del día" url={`${urlCarta}/hoy`} archivo="qr-menu-del-dia" />
      </Space>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
        Imprime el QR de la carta para las mesas, o el del menú del día si trabajas con comida
        corrida — ese se actualiza solo cada mañana con lo que captures en Menú del Día.
        Con <strong>PDF</strong> descargas el menú tal como se ve, listo para imprimir o mandar
        por WhatsApp; tus clientes no ven ese botón, es solo para ti.
      </Text>
    </>
  ) : (
    <Text type="secondary">Guarda primero la dirección pública para generar tus QRs.</Text>
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {!config?.slug && (
        <Alert
          type="info"
          showIcon
          message="Elige la dirección de tu menú para empezar"
          description="Es la liga que llevan tus códigos QR. Después podrás elegir plantilla, colores y qué productos mostrar."
        />
      )}

      {/* La dirección va suelta y arriba cuando aún no existe; una vez
          configurada se guarda con el resto del arreglo, que es cosa de una
          sola vez, y el editor se queda con el protagonismo. */}
      {!config?.slug ? (
        <Card title="Dirección de tu menú">{direccion}</Card>
      ) : (
        <Collapse
          items={[
            {
              key: 'ajustes',
              label: 'Dirección, diseño y códigos QR',
              children: (
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  <div>{direccion}</div>
                  <div>{apariencia}</div>
                  <div>{codigos}</div>
                </Space>
              ),
            },
          ]}
        />
      )}

      {editorLoading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : editorMenu && editorMenu.categorias.length > 0 ? (
        <MenuEditorCanvas restaurantId={restaurantId} menu={editorMenu} />
      ) : (
        <Alert
          type="warning"
          showIcon
          message="Todavía no hay nada que mostrar"
          description="Da de alta productos en la pestaña Productos y aquí podrás elegir cuáles publicar y en qué orden."
        />
      )}
    </Space>
  );
};

export default MenuDigitalTab;
